import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Card, Badge, Button, Tabs, Tab, Spinner, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Clock, 
  FileText, 
  CheckCircle, 
  XCircle, 
  MessageCircle,
  AlertCircle,
  Download,
  ChevronRight,
  CalendarClock
} from 'lucide-react';
import { AuthContext } from '../contexts/AuthContext';
import { useContractStatus } from '../contexts/ContractStatusContext';
import api from '../api';
import './ContractTracker.css';

const ContractTracker = () => {
  const { auth } = useContext(AuthContext);
  const { getStatus } = useContractStatus();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('negotiating');
  const [contracts, setContracts] = useState({
    negotiating: [],
    pending: [],
    completed: [],
    declined: []
  });

  // Fetch contracts data
  useEffect(() => {
    const fetchContracts = async () => {
      if (!auth?.token) return;
      
      try {
        setLoading(true);
        
        // In a real implementation, this would be an API call
        // For demonstration, we'll simulate the data
        
        // Simulated contracts data
        const simulatedContracts = {
          negotiating: [
            {
              _id: 'contract1',
              listingName: 'Lead CRNA at City General',
              facilityName: 'City General Hospital',
              facilityLocation: 'Chicago, IL',
              status: 'negotiating',
              startDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
              compensation: {
                amount: 230000,
                period: 'annual'
              },
              lastUpdate: {
                type: 'compensation_update',
                date: new Date(Date.now() - 12 * 60 * 60 * 1000),
                message: 'Facility increased offer to $230,000/yr'
              },
              matchedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)
            },
            {
              _id: 'contract2',
              listingName: 'Weekend CRNA Position',
              facilityName: 'Riverside Medical Center',
              facilityLocation: 'Portland, OR',
              status: 'negotiating',
              startDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000),
              compensation: {
                amount: 220,
                period: 'hourly'
              },
              lastUpdate: {
                type: 'schedule_update',
                date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
                message: 'Adjusted schedule to alternating weekends'
              },
              matchedAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000)
            }
          ],
          pending: [
            {
              _id: 'contract3',
              listingName: 'CRNA at North Valley Surgery',
              facilityName: 'North Valley Surgery Center',
              facilityLocation: 'Phoenix, AZ',
              status: 'pending_signature',
              startDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
              compensation: {
                amount: 205000,
                period: 'annual'
              },
              lastUpdate: {
                type: 'contract_ready',
                date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
                message: 'Contract is ready for your signature'
              },
              matchedAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000)
            }
          ],
          completed: [
            {
              _id: 'contract4',
              listingName: 'Senior CRNA Role',
              facilityName: 'Memorial Hospital',
              facilityLocation: 'Boston, MA',
              status: 'completed',
              startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
              endDate: new Date(Date.now() + 335 * 24 * 60 * 60 * 1000),
              compensation: {
                amount: 245000,
                period: 'annual'
              },
              lastUpdate: {
                type: 'contract_signed',
                date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
                message: 'Contract fully executed by all parties'
              },
              matchedAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)
            }
          ],
          declined: [
            {
              _id: 'contract5',
              listingName: 'Pediatric CRNA',
              facilityName: 'Children\'s Medical Center',
              facilityLocation: 'Dallas, TX',
              status: 'declined',
              compensation: {
                amount: 180000,
                period: 'annual'
              },
              lastUpdate: {
                type: 'declined',
                date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
                message: 'You declined this offer due to compensation'
              },
              matchedAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000),
              reasonDeclined: 'Compensation was below market rate'
            }
          ]
        };

        // Apply saved statuses from localStorage
        Object.values(simulatedContracts).forEach(list => {
          list.forEach(c => {
            const saved = getStatus(c._id);
            if (saved) {
              c.status = saved;
            }
          });
        });

        // Reorganize contracts based on status
        const organized = { negotiating: [], pending: [], completed: [], declined: [] };
        Object.values(simulatedContracts).forEach(list => {
          list.forEach(c => {
            if (organized[c.status]) {
              organized[c.status].push(c);
            }
          });
        });

        setContracts(organized);
      } catch (err) {
        console.error('Error fetching contracts:', err);
        setError('Failed to load contract data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchContracts();
  }, [auth?.token]);

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading contracts...</span>
        </Spinner>
        <p className="mt-3">Loading your contracts...</p>
      </Container>
    );
  }

  return (
    <Container className="contract-tracker py-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-4"
      >
        <h1 className="page-title">Contract Tracker</h1>
        <p className="page-subtitle">
          Track the status of your negotiations and contracts
        </p>
      </motion.div>
      
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          id="contract-tabs"
          className="mb-4 contract-tabs"
        >
          <Tab 
            eventKey="negotiating" 
            title={
              <span>
                <MessageCircle size={16} className="me-2" />
                Negotiating
                {contracts.negotiating.length > 0 && (
                  <Badge bg="primary" className="ms-2">{contracts.negotiating.length}</Badge>
                )}
              </span>
            }
          >
            <ContractList 
              contracts={contracts.negotiating} 
              type="negotiating"
              navigate={navigate}
            />
          </Tab>
          <Tab 
            eventKey="pending" 
            title={
              <span>
                <FileText size={16} className="me-2" />
                Pending Signature
                {contracts.pending.length > 0 && (
                  <Badge bg="warning" className="ms-2">{contracts.pending.length}</Badge>
                )}
              </span>
            }
          >
            <ContractList 
              contracts={contracts.pending} 
              type="pending"
              navigate={navigate}
            />
          </Tab>
          <Tab 
            eventKey="completed" 
            title={
              <span>
                <CheckCircle size={16} className="me-2" />
                Completed
                {contracts.completed.length > 0 && (
                  <Badge bg="success" className="ms-2">{contracts.completed.length}</Badge>
                )}
              </span>
            }
          >
            <ContractList 
              contracts={contracts.completed} 
              type="completed"
              navigate={navigate}
            />
          </Tab>
          <Tab 
            eventKey="declined" 
            title={
              <span>
                <XCircle size={16} className="me-2" />
                Declined
                {contracts.declined.length > 0 && (
                  <Badge bg="secondary" className="ms-2">{contracts.declined.length}</Badge>
                )}
              </span>
            }
          >
            <ContractList 
              contracts={contracts.declined} 
              type="declined"
              navigate={navigate}
            />
          </Tab>
        </Tabs>
      </motion.div>
      
      <Row className="mt-5">
        <Col>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center"
          >
            <p className="mb-3">
              Looking for more opportunities to negotiate?
            </p>
            <Button 
              variant="primary"
              onClick={() => navigate('/provider-matches')}
            >
              Explore Your Matches
            </Button>
          </motion.div>
        </Col>
      </Row>
    </Container>
  );
};

// Contract List Component
const ContractList = ({ contracts, type, navigate }) => {
  if (contracts.length === 0) {
    return (
      <Card className="text-center p-4">
        <Card.Body>
          <div className="empty-contract-state">
            {type === 'negotiating' && <MessageCircle size={32} className="mb-3" />}
            {type === 'pending' && <FileText size={32} className="mb-3" />}
            {type === 'completed' && <CheckCircle size={32} className="mb-3" />}
            {type === 'declined' && <XCircle size={32} className="mb-3" />}
            
            <h4>No {getContractTypeLabel(type)} Contracts</h4>
            <p>
              {getEmptyStateMessage(type)}
            </p>
            
            {type === 'negotiating' && (
              <Button 
                variant="primary" 
                className="mt-2"
                onClick={() => navigate('/provider-matches')}
              >
                View Your Matches
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="contract-list">
      <Row>
        {contracts.map(contract => (
          <Col md={6} key={contract._id} className="mb-4">
            <motion.div 
              className="contract-card"
              whileHover={{ scale: 1.02 }}
            >
              <div className="contract-card-header">
                <h3>{contract.listingName}</h3>
                {getStatusBadge(contract.status)}
              </div>
              
              <div className="facility-info">
                <div className="facility-name">{contract.facilityName}</div>
                <div className="facility-location">{contract.facilityLocation}</div>
              </div>
              
              <div className="contract-details">
                {contract.compensation && (
                  <div className="compensation">
                    <span className="label">Compensation:</span>
                    <span className="value">
                      {formatCompensation(contract.compensation.amount, contract.compensation.period)}
                    </span>
                  </div>
                )}
                
                {contract.startDate && (
                  <div className="date-info">
                    <CalendarClock size={14} className="me-1" />
                    <span>
                      Start date: {formatDate(contract.startDate)}
                    </span>
                  </div>
                )}
                
                {contract.endDate && (
                  <div className="date-info">
                    <CalendarClock size={14} className="me-1" />
                    <span>
                      End date: {formatDate(contract.endDate)}
                    </span>
                  </div>
                )}
                
                {contract.reasonDeclined && (
                  <div className="reason-declined">
                    <span className="label">Reason:</span>
                    <span className="value">{contract.reasonDeclined}</span>
                  </div>
                )}
              </div>
              
              <div className="last-update">
                <div className="update-icon">
                  {getUpdateTypeIcon(contract.lastUpdate.type)}
                </div>
                <div className="update-content">
                  <p>{contract.lastUpdate.message}</p>
                  <span className="update-time">{formatTimeAgo(contract.lastUpdate.date)}</span>
                </div>
              </div>
              
              <div className="contract-card-actions">
                {type === 'negotiating' && (
                  <>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => navigate(`/messages/${contract._id}`)}
                    >
                      <MessageCircle size={14} className="me-1" />
                      Messages
                    </Button>
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => navigate(`/contract-details/${contract._id}`)}
                    >
                      View Details
                      <ChevronRight size={14} className="ms-1" />
                    </Button>
                  </>
                )}
                
                {type === 'pending' && (
                  <>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => navigate(`/contract-preview/${contract._id}`)}
                    >
                      <FileText size={14} className="me-1" />
                      Preview
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => navigate(`/contracts/${contract._id}/sign`)}
                    >
                      Sign Contract
                      <ChevronRight size={14} className="ms-1" />
                    </Button>
                  </>
                )}
                
                {type === 'completed' && (
                  <>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => {/* View details */}}
                    >
                      <FileText size={14} className="me-1" />
                      View Contract
                    </Button>
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => {/* Download PDF */}}
                    >
                      <Download size={14} className="me-1" />
                      Download PDF
                    </Button>
                  </>
                )}
                
                {type === 'declined' && (
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => navigate(`/contract-details/${contract._id}`)}
                  >
                    View Details
                    <ChevronRight size={14} className="ms-1" />
                  </Button>
                )}
              </div>
            </motion.div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

// Helper functions
const getContractTypeLabel = (type) => {
  switch (type) {
    case 'negotiating': return 'In Negotiation';
    case 'pending': return 'Pending Signature';
    case 'completed': return 'Completed';
    case 'declined': return 'Declined';
    default: return '';
  }
};

const getEmptyStateMessage = (type) => {
  switch (type) {
    case 'negotiating':
      return 'Start negotiating with your matches to see contracts here.';
    case 'pending':
      return 'No contracts are currently waiting for your signature.';
    case 'completed':
      return 'Completed contracts will appear here.';
    case 'declined':
      return 'Declined offers and contracts will be shown here.';
    default:
      return '';
  }
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'negotiating':
      return <Badge bg="primary">Negotiating</Badge>;
    case 'pending_signature':
      return <Badge bg="warning">Pending Signature</Badge>;
    case 'completed':
      return <Badge bg="success">Active Contract</Badge>;
    case 'declined':
      return <Badge bg="danger">Declined</Badge>;
    default:
      return <Badge bg="secondary">Unknown</Badge>;
  }
};

const getUpdateTypeIcon = (type) => {
  switch (type) {
    case 'compensation_update':
      return <AlertCircle size={16} className="update-icon-dollar" />;
    case 'schedule_update':
      return <Clock size={16} className="update-icon-clock" />;
    case 'contract_ready':
      return <FileText size={16} className="update-icon-document" />;
    case 'contract_signed':
      return <CheckCircle size={16} className="update-icon-check" />;
    case 'declined':
      return <XCircle size={16} className="update-icon-x" />;
    default:
      return <MessageCircle size={16} className="update-icon-message" />;
  }
};

const formatCompensation = (amount, period) => {
  if (!amount) return '';
  
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
  
  if (period === 'hourly' || period === 'hour') {
    return `${formatter.format(amount)}/hr`;
  } else if (period === 'annual' || period === 'annually' || period === 'yearly' || period === 'year') {
    return `${formatter.format(amount)}/yr`;
  }
  
  return formatter.format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  }).format(date);
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
  
  if (diffInHours < 1) return 'just now';
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInHours < 48) return 'yesterday';
  
  return formatDate(dateString);
};

export default ContractTracker;
